// src/pages/About.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Leaf, ArrowLeft, Heart, Target, Users, Award, CheckCircle,
  TrendingUp, Shield, Star
} from 'lucide-react';

const About = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, 'settings', 'about-page');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setConfig(docSnap.data());
        }
      } catch (error) {
        console.error('Error fetching about:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-body-bg)' }}>
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
          style={{ borderColor: 'var(--color-link)', borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  // Default fallback
  const data = config || {
    active: true,
    heroTitle: 'About Organic Land',
    heroSubtitle: 'Fresh. Organic. Trusted.',
    heroDescription: 'Hum Pakistan ke sab se behtareen organic products deliver karte hain — directly farms se aapke ghar tak.',
    storyTitle: 'Our Story',
    storyText: 'Organic Land ki shuruaat 2020 mein hui thi, jab humne socha ke logon ko fresh, chemical-free products milne chahiye. Aaj hum 5000+ customers ko serve kar rahe hain.',
    missionTitle: 'Our Mission',
    missionText: 'Har ghar tak fresh organic products pohanchana — affordable price par, quality ke saath.',
    visionTitle: 'Our Vision',
    visionText: 'Pakistan ka sab se trusted organic brand banna.',
    stats: [
      { value: '5,000+', label: 'Happy Customers' },
      { value: '500+', label: 'Organic Products' },
      { value: '50+', label: 'Partner Farms' },
      { value: '4.9', label: 'Rating' },
    ],
    values: [
      { icon: 'leaf', title: '100% Organic', description: 'Certified organic products' },
      { icon: 'heart', title: 'Customer First', description: 'Aapki satisfaction hamari priority' },
      { icon: 'shield', title: 'Trusted Quality', description: 'Har product quality checked' },
      { icon: 'trending', title: 'Always Fresh', description: 'Farm to home delivery' },
    ],
  };

  if (data.active === false) {
    navigate('/');
    return null;
  }

  const renderIcon = (iconName, size = 28) => {
    const props = { size, style: { color: 'var(--color-link)' } };
    switch (iconName) {
      case 'leaf': return <Leaf {...props} />;
      case 'heart': return <Heart {...props} />;
      case 'shield': return <Shield {...props} />;
      case 'trending': return <TrendingUp {...props} />;
      case 'users': return <Users {...props} />;
      case 'award': return <Award {...props} />;
      case 'star': return <Star {...props} />;
      case 'target': return <Target {...props} />;
      default: return <CheckCircle {...props} />;
    }
  };

  return (
    <div className="min-h-screen anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)' }}>

      {/* Back */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-6">
        <button onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full font-semibold text-sm btn-2d shadow-sm"
          style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}>
          <ArrowLeft size={16} /> Back
        </button>
      </div>

      {/* HERO */}
      <section className="py-12 md:py-20 px-4 text-center relative overflow-hidden">
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 mb-5 anim-fadeInUp">
            <Leaf size={16} style={{ color: 'var(--color-link)' }} />
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-link)' }}>
              About Us
            </span>
          </div>

          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight anim-fadeInUp delay-100"
            style={{ color: 'var(--color-primary-text)' }}>
            {data.heroTitle}
          </h1>

          {data.heroSubtitle && (
            <p className="text-lg md:text-2xl font-semibold mb-4 anim-fadeInUp delay-200"
              style={{ color: 'var(--color-link)' }}>
              {data.heroSubtitle}
            </p>
          )}

          {data.heroDescription && (
            <p className="text-base md:text-lg max-w-2xl mx-auto anim-fadeInUp delay-300"
              style={{ color: 'var(--color-secondary-text)' }}>
              {data.heroDescription}
            </p>
          )}
        </div>
      </section>

      {/* STATS */}
      {data.stats && data.stats.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {data.stats.map((stat, i) => (
              <div key={i}
                className="rounded-2xl p-5 md:p-6 text-center card-lift anim-scaleIn"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  border: `1px solid var(--color-border)`,
                  animationDelay: `${i * 100}ms`,
                }}>
                <p className="text-3xl md:text-4xl font-bold mb-2"
                  style={{ color: 'var(--color-link)' }}>
                  {stat.value}
                </p>
                <p className="text-xs md:text-sm font-semibold"
                  style={{ color: 'var(--color-secondary-text)' }}>
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* STORY */}
      {data.storyText && (
        <section className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="rounded-3xl p-6 md:p-10 anim-fadeInUp"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
            <h2 className="text-2xl md:text-3xl font-bold mb-4 flex items-center gap-2"
              style={{ color: 'var(--color-primary-text)' }}>
              <Heart size={24} style={{ color: 'var(--color-link)' }} />
              {data.storyTitle || 'Our Story'}
            </h2>
            <p className="text-sm md:text-base leading-relaxed whitespace-pre-line"
              style={{ color: 'var(--color-secondary-text)' }}>
              {data.storyText}
            </p>
          </div>
        </section>
      )}

      {/* MISSION + VISION */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {data.missionText && (
            <div className="rounded-3xl p-6 md:p-8 anim-fadeInLeft"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                style={{ backgroundColor: 'rgba(22, 163, 74, 0.1)' }}>
                <Target size={26} style={{ color: 'var(--color-link)' }} />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--color-primary-text)' }}>
                {data.missionTitle || 'Our Mission'}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--color-secondary-text)' }}>
                {data.missionText}
              </p>
            </div>
          )}

          {data.visionText && (
            <div className="rounded-3xl p-6 md:p-8 anim-fadeInRight"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                style={{ backgroundColor: 'rgba(250, 204, 21, 0.15)' }}>
                <Star size={26} style={{ color: '#b45309' }} />
              </div>
              <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--color-primary-text)' }}>
                {data.visionTitle || 'Our Vision'}
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'var(--color-secondary-text)' }}>
                {data.visionText}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* VALUES */}
      {data.values && data.values.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-16">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-bold mb-3" style={{ color: 'var(--color-primary-text)' }}>
              Our Values
            </h2>
            <p className="text-sm md:text-base" style={{ color: 'var(--color-secondary-text)' }}>
              Jo cheezein humein sab se alag banati hain
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.values.map((val, i) => (
              <div key={i}
                className="rounded-2xl p-6 text-center card-lift anim-fadeInUp"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  border: `1px solid var(--color-border)`,
                  animationDelay: `${i * 100}ms`,
                }}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: 'rgba(22, 163, 74, 0.1)' }}>
                  {renderIcon(val.icon)}
                </div>
                <h3 className="font-bold text-lg mb-2" style={{ color: 'var(--color-primary-text)' }}>
                  {val.title}
                </h3>
                <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                  {val.description}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="h-20 md:h-0"></div>
    </div>
  );
};

export default About;